import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { cloneDeep, filter, find, findIndex, isEqual } from "lodash";
import CheckmarkOutline from "../icons/Checkmark_Outline";
import CloseOutline from "../icons/Close_Outline";
import ViewFill from "../icons/View_Fill";
import ViewOffFill from "../icons/View-Off_Fill";
import { useTranslation } from 'react-i18next';
import { setFormData } from "../store/slices/Activation/formSlice";

const PasswordInput = () =>
{
  const { t } = useTranslation();
  const dispatch = useDispatch()
  const { form } = useSelector((state) => state.form);
  const [passwordValue, setPasswordValue] = useState("")
  const [reenterPasswordValue, setReenterPasswordValue] = useState("")
  const [passwordShow, setPasswordShow] = useState(false)
  const [resetPasswordShow, setResetPasswordShow] = useState(false)
  const [open, setOpen] = React.useState(false);
  const [mismatchOpen, setMismatchOpen] = useState(false);
  const isValidPasswordLength = (value) => /^.{16,64}$/.test(value);
  const [passwordRules, setPasswordRules] = useState([
    {
      label: t('widget.password.passwordRules.lowercaseRule'),
      pattern: new RegExp(/.*[a-z]/),
      valid: false,
      name: "lowercase"
    },
    {
      label: t('widget.password.passwordRules.uppercaseRule'),
      pattern: new RegExp(/.*[A-Z]/),
      valid: false,
      name: "uppercase"
    },
    {
      label: t('widget.password.passwordRules.numberRule'),
      pattern: new RegExp(/.*\d/),
      valid: false,
      name: "number"
    },
    {
      label: t('widget.password.passwordRules.specialCharRule'),
      pattern: new RegExp(/.*[-'/`~!#*$@_%+=.,^&(){}[\]|;:"<>?]/),
      valid: false,
      name: "specialCharacter"
    },
  ])

  /**
   * on Change function for the first password input field
   * @param   {string} val  Input value
   */
  const handlePasswordChange = (val) =>
  {
    setPasswordValue(val)
    let rules = passwordRules
    rules.forEach((rule) =>
    {
      if (rule.pattern.test(val))
      {
        rule.valid = true
      }
      else
      {
        rule.valid = false
      }
    })
    setPasswordRules(rules)

    const formData = cloneDeep(form)
    let passwordIndex = findIndex(formData, { field: "password" })
    if ((isValidPasswordLength(val) && filter(passwordRules, { valid: true }).length > 2) && isEqual(val, reenterPasswordValue))
    {
      formData[passwordIndex].value = val
    }
    else
    {
      formData[passwordIndex].value = ""
    }
    dispatch(setFormData(formData))
  }

  /**
   * on Change function for the second password input field
   * @param   {string} val  Input value
   */
  const handleReenterPasswordChange = (val) =>
  {
    setReenterPasswordValue(val)
    const formData = cloneDeep(form)
    let passwordIndex = findIndex(formData, { field: "password" })
    if ((isValidPasswordLength(passwordValue) && filter(passwordRules, { valid: true }).length > 2) && isEqual(passwordValue, val))
    {
      formData[passwordIndex].value = val
    }
    else
    {
      formData[passwordIndex].value = ""
    }
    dispatch(setFormData(formData))
  }

  const isPasswordValid = isValidPasswordLength(passwordValue) && filter(passwordRules, { valid: true }).length > 2
  const isPasswordMismatch = reenterPasswordValue.length > 0 && !isEqual(passwordValue, reenterPasswordValue)
  const isPasswordCriteriaUnmet = passwordValue.length > 0 && !isPasswordValid

  useEffect(() =>
  {
    setMismatchOpen(isPasswordMismatch)
  }, [isPasswordMismatch])

  return (
    <>
      <div className="col-md-24 form-group">
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div>
            {find(form, { field: "password" }).error &&
              <div role="alert">
                <span className="alert alert-error" id="attrError2">{find(form, { field: "password" }).errorMessage}</span>
              </div>}
            <Tooltip
              placement="right"
              PopperProps={{
                disablePortal: true,
              }}
              onClose={() => setOpen(false)}
              open={open}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={<div className="password-req-tooltip">
                <p>{isValidPasswordLength(passwordValue) ? <CheckmarkOutline stroke="#22992e" /> : <CloseOutline stroke="#e81123" />}&nbsp;{t('widget.password.passwordRules.lengthRule')}</p>
                <p>{t('widget.password.passwordRules.ruleLimit')}</p>
                {passwordRules.map((rule) =>
                  <p key={rule.name} style={{ marginLeft: '16px' }}>{rule.valid ? <CheckmarkOutline stroke="#22992e" /> : <CloseOutline stroke="#e81123" />}&nbsp;{rule.label}</p>
                )}
              </div>}>
              <div className={`placeholderContainer password-display display-sign-container border-bottom ${(find(form, { field: "password" }).error || isPasswordCriteriaUnmet) && "has-error"}`}>
                <input
                  type={passwordShow ? "text" : "password"}
                  autoComplete="off"
                  className="form-control text-box"
                  id="ipasswordInput"
                  placeholder={t('widget.password.passwordPlaceholder')}
                  value={passwordValue}
                  onChange={(e) => { handlePasswordChange(e.target.value) }}
                  minLength={16}
                  onClick={() => setOpen(true)}
                />
                  {isPasswordCriteriaUnmet && <CloseOutline stroke="#e81123" className="margin-rt-4" />}
                  {isPasswordValid && <CheckmarkOutline stroke="#22992e" className="margin-rt-4" />}
                <span className="display-flex display-showButton" role="button" aria-label="Password show or hide" tabIndex={0}
                style={{ cursor: 'pointer'}} onClick={() => setPasswordShow(!passwordShow)}onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setPasswordShow(!passwordShow);
                  }
                }}>{passwordShow ? <ViewFill /> : <ViewOffFill />}</span>
              </div>
            </Tooltip>
          </div>
        </ClickAwayListener>
      </div>
      <div className="col-md-24 form-group">
        <ClickAwayListener onClickAway={() => setMismatchOpen(false)}>
          <div>
            <Tooltip
              placement="right"
              PopperProps={{
                disablePortal: true,
              }}
              onClose={() => setMismatchOpen(false)}
              open={mismatchOpen && isPasswordMismatch}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={<div className="password-req-tooltip">
                <p><CloseOutline stroke="#e81123" />&nbsp;Passwords do not match.</p>
              </div>}>
              <div className={`placeholderContainer password-display display-sign-container border-bottom ${(find(form, { field: "password" }).error || isPasswordMismatch) && "has-error"}`}>
                <input
                  type={resetPasswordShow ? "text" : "password"}
                  autoComplete="off"
                  className="form-control text-box"
                  id="ipasswordConfirmationInput"
                  placeholder={t('widget.password.reenterPasswordPlaceholder')}
                  onChange={(e) => { handleReenterPasswordChange(e.target.value) }}
                  onClick={() =>
                  {
                    if (isPasswordMismatch)
                    {
                      setMismatchOpen(true)
                    }
                  }}
                  aria-invalid={isPasswordMismatch}
                />
                {(isPasswordValid && !isPasswordMismatch && reenterPasswordValue.length > 0) && <CheckmarkOutline stroke="#22992e" className="green-check margin-rt-4" />}
                {isPasswordMismatch && <CloseOutline stroke="#e81123" className="margin-rt-4" />}
                <span className="display-flex display-showButton" aria-label="Password show or hide" style={{ cursor: 'pointer' }} role="button" tabIndex={0} onClick={() => setResetPasswordShow(!resetPasswordShow)}onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setResetPasswordShow(!resetPasswordShow);
                  }
                }}>{resetPasswordShow ? <ViewFill /> : <ViewOffFill />}</span>
              </div>
            </Tooltip>
          </div>
        </ClickAwayListener>
      </div>
    </>
  );
};

export default PasswordInput;
